type ViewportMetricValue = boolean | number | string

export type SignalsKitViewportMetrics = Record<string, ViewportMetricValue>

const round = (value: number) => Math.round(value)

const roundRatio = (value: number) => Math.round(value * 1000) / 1000

export const getScrollDepthPercent = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return 0
  }

  const scrollable = document.documentElement.scrollHeight - window.innerHeight
  if (scrollable <= 0) return 0

  return Math.max(0, Math.min(100, round((window.scrollY / scrollable) * 100)))
}

export const getElementViewportMetrics = (
  element?: Element | null,
  entry?: IntersectionObserverEntry | null
): SignalsKitViewportMetrics => {
  if (typeof window === 'undefined') {
    return {}
  }

  const rect = entry?.boundingClientRect || element?.getBoundingClientRect()
  if (!rect) {
    return {
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      scroll_y: round(window.scrollY || 0),
      scroll_depth_percent: getScrollDepthPercent(),
      is_in_viewport: false
    }
  }

  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight
  const visibleLeft = Math.max(0, rect.left)
  const visibleRight = Math.min(viewportWidth, rect.right)
  const visibleTop = Math.max(0, rect.top)
  const visibleBottom = Math.min(viewportHeight, rect.bottom)
  const visibleWidth = Math.max(0, visibleRight - visibleLeft)
  const visibleHeight = Math.max(0, visibleBottom - visibleTop)
  const elementArea = Math.max(1, rect.width * rect.height)
  const visibleArea = visibleWidth * visibleHeight
  const visibleRatio = entry?.intersectionRatio ?? Math.max(0, Math.min(1, visibleArea / elementArea))
  const viewportBand = rect.bottom <= 0
    ? 'above_viewport'
    : rect.top >= viewportHeight
      ? 'below_viewport'
      : rect.top < viewportHeight * 0.33
        ? 'top_third'
        : rect.top < viewportHeight * 0.66
          ? 'middle_third'
          : 'bottom_third'

  return {
    viewport_width: viewportWidth,
    viewport_height: viewportHeight,
    scroll_y: round(window.scrollY || 0),
    scroll_depth_percent: getScrollDepthPercent(),
    element_top: round(rect.top),
    element_bottom: round(rect.bottom),
    element_left: round(rect.left),
    element_right: round(rect.right),
    element_width: round(rect.width),
    element_height: round(rect.height),
    visible_width: round(visibleWidth),
    visible_height: round(visibleHeight),
    visible_ratio: roundRatio(visibleRatio),
    visible_percent: round(visibleRatio * 100),
    viewport_band: viewportBand,
    is_in_viewport: visibleRatio > 0,
    is_fully_visible: visibleRatio >= 0.98
  }
}

export const getTimingMetrics = (startedAt?: number | null): SignalsKitViewportMetrics => {
  if (typeof window === 'undefined') {
    return {}
  }

  const now = Date.now()
  const navigationStartedAt = performance?.timeOrigin || now

  return {
    time_on_page_ms: Math.max(0, round(now - navigationStartedAt)),
    time_since_start_ms: startedAt ? Math.max(0, round(now - startedAt)) : 0
  }
}
